import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import Home from "@/pages/Home";
import CreateCandle from "@/pages/CreateCandle";
import Memorial from "@/pages/Memorial";
import MyCandles from "@/pages/MyCandles";
import FamilyGroups from "@/pages/FamilyGroups";
import GroupMemories from "@/pages/GroupMemories";
import Messages from "@/pages/Messages";
import Profil from "@/pages/Profil";
import Parrainage from "@/pages/Parrainage";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Toussaint from "@/pages/Toussaint";
import ToussaintLanding from "@/pages/ToussaintLanding";
import Admin from "@/pages/Admin";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Conditions from "@/pages/Conditions";
import Ambassador from "@/pages/Ambassador";
import LivingCandle from "@/pages/LivingCandle";
import LivingIntro from "@/pages/LivingIntro";
import CGU from "@/pages/CGU";
import PublishLiving from "@/pages/PublishLiving";
import MessageAccess from "@/pages/MessageAccess";
import CeremonyAccess from "@/pages/CeremonyAccess";
import { BottomNav } from "@/components/BottomNav";
import { InstallPrompt } from "@/components/InstallPrompt";
import { api } from "@/lib/api";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const AnalyticsTracker = () => {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/admin")) return; // on ne compte pas les pages admin
    let sid = sessionStorage.getItem("bdc_sid");
    if (!sid) { sid = (crypto.randomUUID?.() || String(Date.now() + Math.random())); sessionStorage.setItem("bdc_sid", sid); }
    let vid = localStorage.getItem("bdc_vid");
    let isNew = false;
    if (!vid) { vid = (crypto.randomUUID?.() || String(Date.now() + Math.random())); localStorage.setItem("bdc_vid", vid); isNew = true; }
    api.post("/analytics/track", { path, referrer: document.referrer || "", session_id: sid, visitor_id: vid, is_new: isNew }).catch(() => {});
  }, [location.pathname]);

  // Offre ambassadeur privée : mémorise le code (silencieux) puis mène directement à l'inscription
  useEffect(() => {
    const code = new URLSearchParams(location.search).get("amb");
    if (!code) return;
    const c = code.trim().toUpperCase();
    try { localStorage.setItem("bdc_amb_code", c); } catch (e) { /* ignore */ }
    api.get(`/ambassador/${c}/track`).catch(() => {});
    const token = (() => { try { return localStorage.getItem("bdc_token"); } catch (e) { return null; } })();
    if (!token) navigate("/?auth=register", { replace: true });
  }, [location.search, navigate]);

  return null;
};

const AuthCallback = () => {
  const { googleLogin } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!sessionId) { navigate("/"); return; }
    (async () => {
      let u = null;
      try {
        u = await googleLogin(sessionId);
      } catch (e) { /* ignore */ }
      window.history.replaceState(null, "", window.location.pathname);
      let dest = "/creer";
      if (u?.is_ambassador && (u.free_eternal_candles || 0) > 0) {
        dest = "/ambassadeur";
      } else {
        try {
          const saved = localStorage.getItem("bdc_post_login_redirect");
          if (saved) { dest = saved; localStorage.removeItem("bdc_post_login_redirect"); }
        } catch (e) { /* ignore */ }
      }
      navigate(dest);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex h-[100dvh] items-center justify-center">
      <Loader2 className="animate-spin text-sky-400" />
    </div>
  );
};

const ProfileReminder = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const shown = useRef(false);
  useEffect(() => {
    if (!user) { shown.current = false; return; }
    if ((user.free_eternal_candles || 0) > 0) return; // ne pas gêner le parcours ambassadeur
    const incomplete = !user.first_name || !user.last_name || !user.phone || !user.country;
    const key = `bdc_profile_reminder_${user.id}`;
    if (incomplete && !shown.current && !sessionStorage.getItem(key)) {
      shown.current = true;
      sessionStorage.setItem(key, "1");
      toast(t("profile.incompleteNotice"), {
        duration: 9000,
        action: { label: t("profile.complete"), onClick: () => navigate("/profil") },
      });
    }
  }, [user, navigate, t]);
  return null;
};

// Bascule le manifest PWA + apple-touch-icon selon la page.
// Sur /postinity (et ses sous-pages), l'app installée utilise l'icône et le nom Postinity.
const PwaManifestSwitcher = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const isPostinity = pathname.startsWith("/postinity");
    const manifestHref = isPostinity ? "/postinity-manifest.json" : "/manifest.json";
    const appleHref = isPostinity ? "/postinity-apple-touch.png" : "/apple-touch-icon.png";
    const appTitle = isPostinity ? "Postinity" : "BougieDuCœur";

    const manifestEl = document.querySelector('link[rel="manifest"]');
    if (manifestEl && manifestEl.getAttribute("href") !== manifestHref) {
      manifestEl.setAttribute("href", manifestHref);
    }
    document.querySelectorAll('link[rel="apple-touch-icon"]').forEach((el) => {
      el.setAttribute("href", appleHref);
    });
    const titleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (titleMeta) titleMeta.setAttribute("content", appTitle);
  }, [pathname]);
  return null;
};

const SeoVerification = () => {
  useEffect(() => {
    let cancelled = false;
    api.get("/settings").then((r) => {
      if (cancelled) return;
      const v = r.data.seo_verification || {};
      const setV = (name, content) => {
        if (!content) return;
        let el = document.head.querySelector(`meta[name="${name}"]`);
        if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
        el.setAttribute("content", content);
      };
      setV("google-site-verification", v.google);
      setV("msvalidate.01", v.bing);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return null;
};

const RefCapture = () => {
  const { user } = useAuth();
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      if (ref) localStorage.setItem("bdc_ref", ref);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => {
    if (!user) return;
    let ref;
    try { ref = localStorage.getItem("bdc_ref"); } catch (e) { ref = null; }
    if (!ref || user.referred_by || user.referral_code === ref) {
      if (ref && (user.referred_by || user.referral_code === ref)) { try { localStorage.removeItem("bdc_ref"); } catch (e) {} }
      return;
    }
    api.post("/me/referral/attribute", { code: ref })
      .catch(() => {})
      .finally(() => { try { localStorage.removeItem("bdc_ref"); } catch (e) {} });
  }, [user]);
  return null;
};

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/creer" element={<CreateCandle />} />
      <Route path="/candle/:id" element={<Memorial />} />
      <Route path="/mes-bougies" element={<MyCandles />} />
      <Route path="/souvenirs" element={<FamilyGroups />} />
      <Route path="/souvenirs/:id" element={<GroupMemories />} />
      <Route path="/famille/:token" element={<GroupMemories shared />} />
      <Route path="/messages" element={<Messages />} />
      <Route path="/profil" element={<Profil />} />
      <Route path="/ma-bougie" element={<LivingIntro />} />
      <Route path="/postinity" element={<LivingIntro />} />
      <Route path="/postinity/preparer" element={<LivingCandle />} />
      <Route path="/cgu" element={<CGU />} />
      <Route path="/hommage/:code" element={<PublishLiving />} />
      <Route path="/message/:code" element={<MessageAccess />} />
      <Route path="/ceremonie/:livingId" element={<CeremonyAccess />} />
      <Route path="/parrainage" element={<Parrainage />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/toussaint" element={<Toussaint />} />
      <Route path="/jour-des-morts" element={<Toussaint />} />
      <Route path="/fete-des-morts" element={<Toussaint />} />
      <Route path="/toussaint-2026" element={<ToussaintLanding />} />
      <Route path="/toussaint-2026/allumer" element={<CreateCandle publicMode />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/conditions" element={<Conditions />} />
      <Route path="/ambassadeur" element={<Ambassador />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CurrencyProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AnalyticsTracker />
            <PwaManifestSwitcher />
            <SeoVerification />
            <RefCapture />
            <AppRouter />
            <BottomNav />
            <ProfileReminder />
            <InstallPrompt />
          </BrowserRouter>
          <Toaster
            position="top-center"
            theme="dark"
            richColors
            offset={{ top: "calc(env(safe-area-inset-top, 0px) + 16px)" }}
            mobileOffset={{ top: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
          />
        </CurrencyProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
